import { useState, useEffect, useMemo } from "react";
import { Form, Button, FormControl, InputGroup, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { BookTicket } from "../services/booking.js";
import { ColorRing } from "react-loader-spinner";
import { usePublicContent } from "../lib/publicContent";

const TicketSubscription = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.bookingSlice);
  const { content } = usePublicContent();

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [Day1TicketsAmount, setDay1ticketamount] = useState(0);
  const [Day2TicketsAmount, setDay2ticketamount] = useState(0);
  const [Day3TicketsAmount, setDay3ticketamount] = useState(0);
  const [AllDaysTicketsAmount, setAllDaysTicketsAmount] = useState(0);
  const [SubscribedForFurtherConcertInfos, setSubscribedForFurtherConcertInfos] = useState(true);
  const [isStudent, setIsStudent] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [confirmationMessageTitle, setConfirmationMessageTitle] = useState("");
  const [emailError, setEmailError] = useState("");

  // ---------- prices from CMS (content.tickets.tiers) ----------
  const parsePrice = (v) => {
    if (typeof v === "number") return v;
    if (!v) return NaN;
    const s = String(v).replace(/[^0-9,.\-]/g, "").replace(",", ".");
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
  };

  const tiers = content?.tickets?.tiers || [];
  const findPrice = (matcher) =>
    parsePrice((tiers.find((t) => matcher((t.title || "").toLowerCase())) || {}).price);

  let normalDayPrice =
    findPrice((t) => /normal/.test(t)) ??
    parsePrice(tiers.find((t) => (t.title || "").toLowerCase() === "ticket normalpreis")?.price);

  let studentDayPrice =
    findPrice((t) => /(ermäß|ermaess|ermassig|student)/.test(t)) ??
    parsePrice(tiers.find((t) => (t.title || "").toLowerCase() === "ticket ermäßigt")?.price);

  let comboPrice =
    findPrice((t) => /(kombi|combo|alle|all)/.test(t)) ??
    parsePrice(tiers.find((t) => (t.title || "").toLowerCase() === "kombiticket")?.price);

  if (!Number.isFinite(normalDayPrice)) normalDayPrice = 20;
  if (!Number.isFinite(studentDayPrice)) studentDayPrice = normalDayPrice;
  if (!Number.isFinite(comboPrice)) comboPrice = 50;

  // ---------- schedule labels from CMS — only enabled days ----------
  const allDays = content?.schedule?.days || [];
  const enabledDays = allDays.filter(d => d.enabled !== false);
  const formatDayLabel = (d) => [d?.tabLabel, d?.dateLabel].filter(Boolean).join(" : ");
  // Map positionally to day1/day2/day3 backend fields (max 3)
  const Day1Label = formatDayLabel(enabledDays[0]) || "Samstag : 23.11.2024";
  const Day2Label = formatDayLabel(enabledDays[1]) || "Sonntag : 24.11.2024";
  const Day3Label = formatDayLabel(enabledDays[2]) || "Montag : 25.11.2024";

  // ---------- pricing switch ----------
  const dayPrice = isStudent ? studentDayPrice : normalDayPrice;

  const totalCost = useMemo(() => {
    const dayTicketsTotal =
      (Day1TicketsAmount + Day2TicketsAmount + Day3TicketsAmount) * dayPrice;
    const comboTotal = AllDaysTicketsAmount * comboPrice;
    return (dayTicketsTotal + comboTotal).toFixed(2);
  }, [
    Day1TicketsAmount,
    Day2TicketsAmount,
    Day3TicketsAmount,
    AllDaysTicketsAmount,
    dayPrice,
    comboPrice,
  ]);

  useEffect(() => {
    if (data.message && data.message.trim() !== "") {
      if (data.isBooked) {
        setConfirmationMessageTitle("Reservierungsbestätigung");
        setConfirmationMessage(data.message);
      } else {
        setConfirmationMessageTitle("Fehler bei der Reservierung");
        setConfirmationMessage(data.message);
      }
      setLoading(false);
      setOpacity(1);
      setShowModal(true);
      resetForm();
    }
  }, [data]);

  const toggleModal = () => setShowModal(!showModal);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setDay1ticketamount(0);
    setDay2ticketamount(0);
    setDay3ticketamount(0);
    setAllDaysTicketsAmount(0);
    setSubscribedForFurtherConcertInfos(true);
    setIsStudent(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setOpacity(0.5);
    setLoading(true);
    try {
      BookTicket(dispatch, {
        firstName,
        lastName,
        email,
        Day1TicketsAmount,
        Day2TicketsAmount,
        Day3TicketsAmount,
        AllDaysTicketsAmount,
        SubscribedForFurtherConcertInfos,
        isStudent,
        AppliedDayPrice: dayPrice,
        ComboPrice: comboPrice,
        // NEW: include the day labels taken from CMS
        Day1Label,
        Day2Label,
        Day3Label,
      });
    } catch (error) {
      console.error("Error booking tickets:", error);
      setLoading(false);
      setOpacity(1);
    }
  };

  const activeDayAmounts = [Day1TicketsAmount, Day2TicketsAmount, Day3TicketsAmount].slice(0, enabledDays.length);
  const disableSubmit =
    firstName.trim().length <= 0 ||
    lastName.trim().length <= 0 ||
    email.trim().length <= 0 ||
    !!emailError ||
    activeDayAmounts.some(n => n < 0) ||
    AllDaysTicketsAmount < 0 ||
    (activeDayAmounts.every(n => n === 0) && AllDaysTicketsAmount === 0);

  const isValidEmail = (value) => {
    // RFC 5322 compliant but still human-friendly regex
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  };

  return (
    <>
      <div className="col-lg-8 col-md-12 col-xs-12">
        <div className="container-form wow fadeInLeft" data-wow-delay="0.2s">
          <div className="form-wrapper">
            <div className="loadingSpinnerDivInForm">
              <ColorRing
                visible={loading}
                height="80"
                width="80"
                ariaLabel="blocks-loading"
                colors={["#852280", "#D02106", "#D83C3A", "#FE0000", "#F67F00"]}
              />
            </div>

            <Form onSubmit={handleSubmit} style={{ opacity }}>
              <div className="row mb-3">
                <div className="col-md-6 mb-3">
                  <FormControl
                    className="form-control"
                    placeholder="Vorname"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <FormControl
                    className="form-control"
                    placeholder="Nachname"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <InputGroup className="mb-3">
                <FormControl
                  placeholder="E-Mail"
                  value={email}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEmail(val);
                    if (val.trim() === "") {
                      setEmailError("E-Mail darf nicht leer sein");
                    } else if (!isValidEmail(val)) {
                      setEmailError("Bitte eine gültige E-Mail-Adresse eingeben");
                    } else {
                      setEmailError("");
                    }
                  }}
                  isInvalid={!!emailError}
                />
                <Form.Control.Feedback type="invalid">
                  {emailError}
                </Form.Control.Feedback>

              </InputGroup>

              {/* Student toggle */}
              <Form.Group controlId="studentCheckbox" className="mb-3">
                <Form.Check
                  style={{ margin: "5px 0", color: "#676767", fontSize: "14px" }}
                  type="checkbox"
                  label="Ich bin Student*in/Schüler*in (ermäßigt)"
                  checked={isStudent}
                  onChange={(e) => setIsStudent(e.target.checked)}
                />
                <div className="small text-muted">
                  Preis pro Tag: €{dayPrice.toFixed(2)} • Kombiticket: €{comboPrice.toFixed(2)}
                </div>
              </Form.Group>

              <div className="mb-3">
                <div className="row">
                  {[
                    { label: Day1Label, amount: Day1TicketsAmount, setAmount: setDay1ticketamount },
                    { label: Day2Label, amount: Day2TicketsAmount, setAmount: setDay2ticketamount },
                    { label: Day3Label, amount: Day3TicketsAmount, setAmount: setDay3ticketamount },
                  ].slice(0, enabledDays.length).map(({ label, amount, setAmount }, i) => (
                    <div className="col-md-4" key={i}>
                      <InputGroup.Text className="input-text-below">{label}</InputGroup.Text>
                      <InputGroup className="custom-input-group">
                        <Button
                          variant="outline-secondary custom-tickets-button"
                          style={{ marginTop: "6px" }}
                          onClick={() => setAmount((n) => Math.max(0, n - 1))}
                        >
                          <i className="lni lni-minus" />
                        </Button>
                        <FormControl readOnly type="text" value={amount} style={{ textAlign: "center" }} />
                        <Button
                          variant="outline-secondary custom-tickets-button"
                          style={{ marginLeft: "3px", marginTop: "6px" }}
                          onClick={() => setAmount((n) => n + 1)}
                        >
                          <i className="lni lni-plus" />
                        </Button>
                      </InputGroup>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <Form.Label>Kombiticket (gültig für alle Tage)</Form.Label>
                <InputGroup className="custom-input-group">
                  <Button
                    variant="outline-secondary custom-tickets-button"
                    style={{ marginTop: "6px" }}
                    onClick={() => setAllDaysTicketsAmount((n) => Math.max(0, n - 1))}
                  >
                    <i className="lni lni-minus" />
                  </Button>
                  <FormControl readOnly type="text" value={AllDaysTicketsAmount} style={{ textAlign: "center" }} />
                  <Button
                    variant="outline-secondary custom-tickets-button"
                    style={{ marginLeft: "3px", marginTop: "6px" }}
                    onClick={() => setAllDaysTicketsAmount((n) => n + 1)}
                  >
                    <i className="lni lni-plus" />
                  </Button>
                </InputGroup>
              </div>

              <Form.Group controlId="subscribeCheckbox">
                <Form.Check
                  style={{ margin: "15px auto auto 5px", color: "#676767", fontSize: "14px" }}
                  type="checkbox"
                  label="Ich möchte Informationen zu weiteren Konzerten erhalten."
                  checked={SubscribedForFurtherConcertInfos}
                  onChange={(e) => setSubscribedForFurtherConcertInfos(e.target.checked)}
                />
              </Form.Group>

              <div className="mb-3 ticketAmountSection">
                <Form.Label>Total Cost</Form.Label>
                <div>{totalCost} Euro</div>
              </div>

              <Button
                type="submit"
                variant="success"
                style={{ margin: "15px auto 5px", display: "block", width: "12rem" }}
                className="btn btn-common"
                disabled={disableSubmit}
              >
                <i className="fa fa-paper-plane" aria-hidden="true"> Jetzt reservieren</i>
              </Button>
            </Form>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={toggleModal}>
        <Modal.Header closeButton>
          <Modal.Title>{confirmationMessageTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{confirmationMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" className="btn btn-common" onClick={toggleModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TicketSubscription;
